// 11 Sep 1906
import { createAsyncThunk } from '@reduxjs/toolkit'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  selectVitalsFormSettingsData,
  setVitalsFormSettingsData,
  setVitalsFormSettingsError,
  setVitalsFormSettingsState,
} from '../vitalsFormSettingsReducer'
import {
  REDUX_STATUS_FAILED,
  REDUX_STATUS_LOADING,
  REDUX_STATUS_SUCCESS,
} from 'src/consumer/constants'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'

// Helper function to detect portal and get default API signature
const getDefaultVitalsFormApiSignature = () => {
  if (typeof window === 'undefined') {
    return '/api/secure/practitioner/vitals/form'
  }
  
  const pathname = window.location.pathname
  
  if (pathname.startsWith('/ca-home/') || pathname.startsWith('/kiosk/') || pathname.startsWith('/ca/')) {
    // Clinic Assistant Portal
    return '/api/secure/clinic-assistant/vitals/form'
  }
  if (pathname.startsWith('/support-home/') || pathname.startsWith('/support/')) {
    // Support Portal - use support portal API
    return '/api/secure/cc/support/practitioner/vitals/form'
  }
  if (pathname.startsWith('/mentor-home/') || pathname.startsWith('/mentor/') || pathname.startsWith('/teacher/') || pathname.startsWith('/l/')) {
    // Mentor Portal (including live session routes)
    return '/api/secure/practitioner/vitals/form'
  }
  
  // Default to practitioner API (mentor portal)
  return '/api/secure/practitioner/vitals/form'
}

const inFlightVitalsFormRequests = new Map()
const vitalsFormSettingsCache = new Map()
const VITALS_FORM_SETTINGS_CACHE_TTL_MS = 5 * 60 * 1000

export const fetchVitalsFormSettings = createAsyncThunk(
  'vitalsFormSettingsStore/fetchVitalsFormSettings',
  async ({ apiSignature, practitionerId }, { getState, dispatch }) => {
    // Use provided signature or detect portal-based default
    let finalApiSignature = apiSignature || getDefaultVitalsFormApiSignature()
    
    // Extract practitioner-id from URL if present, or use provided practitionerId
    let extractedPractitionerId = practitionerId
    if (finalApiSignature.includes('practitioner-id=')) {
      const match = finalApiSignature.match(/practitioner-id=([^&]+)/)
      if (match) {
        extractedPractitionerId = match[1]
        // Remove query params from signature for support portal API call
        finalApiSignature = finalApiSignature.split('?')[0]
      }
    }
    
    const state = getState()
    const existingData = selectVitalsFormSettingsData(state)?.data
    const currentState = state?.vitalsFormSettingsStore?.vitalsFormSettings?.state
    const requestKey = `${finalApiSignature}::${extractedPractitionerId || ''}`

    // If data exists and state is already success, return existing data without dispatching
    if (existingData && existingData.length > 0 && currentState === REDUX_STATUS_SUCCESS) {
      vitalsFormSettingsCache.set(requestKey, {
        data: existingData,
        timestamp: Date.now(),
      })
      return existingData
    }

    const cached = vitalsFormSettingsCache.get(requestKey)
    if (cached && Date.now() - cached.timestamp < VITALS_FORM_SETTINGS_CACHE_TTL_MS) {
      dispatch(setVitalsFormSettingsData(cached.data))
      dispatch(setVitalsFormSettingsState(REDUX_STATUS_SUCCESS))
      return cached.data
    }

    if (inFlightVitalsFormRequests.has(requestKey)) {
      return inFlightVitalsFormRequests.get(requestKey)
    }
    
    // Only set loading state if not already loading
    if (currentState !== REDUX_STATUS_LOADING) {
      dispatch(setVitalsFormSettingsState(REDUX_STATUS_LOADING))
    }

    try {
      let resp
      const requestPromise = (async () => {
        // Check if this is a support portal API
        if (finalApiSignature.includes('/cc/support/practitioner/vitals/form')) {
          // Support portal API - requires practitioner-id
          if (!extractedPractitionerId) {
            throw new Error('practitionerId is required for support portal vitals form API')
          }
          resp = await getSPDataWithParams(
            finalApiSignature,
            {
              'practitioner-id': extractedPractitionerId,
            },
            undefined,
            undefined,
            true,
          )
          // The support endpoint returns the field list as a RAW JSON array (no
          // commonResponse envelope), so envelope validation alone rejects a good
          // 200 and the dummy-preview vitals never render. Accept both shapes.
          if (resp?.status === 200 && (Array.isArray(resp?.data) || isCommonResponseSuccessfulV2(resp))) {
            const data = Array.isArray(resp?.data) ? resp.data : resp?.data?.data || resp?.data
            dispatch(setVitalsFormSettingsData(data))
            dispatch(setVitalsFormSettingsState(REDUX_STATUS_SUCCESS))
            vitalsFormSettingsCache.set(requestKey, {
              data,
              timestamp: Date.now(),
            })
            return data
          }
          throw new Error('Invalid response from API')
        }

        // Mentor portal or clinic assistant API
        resp = await getCPDataWithoutParams(finalApiSignature)
        if (resp?.status === 200) {
          // Update store with new data
          dispatch(setVitalsFormSettingsData(resp.data))
          dispatch(setVitalsFormSettingsState(REDUX_STATUS_SUCCESS))
          vitalsFormSettingsCache.set(requestKey, {
            data: resp.data,
            timestamp: Date.now(),
          })
          return resp.data
        }
        throw new Error('Invalid response from API')
      })()

      inFlightVitalsFormRequests.set(requestKey, requestPromise)
      return await requestPromise
    } catch (error) {
      dispatch(setVitalsFormSettingsError(error.message || 'Failed to fetch vitals form settings'))
      dispatch(setVitalsFormSettingsState(REDUX_STATUS_FAILED))
      throw error
    } finally {
      inFlightVitalsFormRequests.delete(requestKey)
    }
  },
)
